import { useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { addPlayer, lookupFidePlayer } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { useApiErrorHandler } from '@/lib/useApiErrorHandler';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { splitFideName } from '@/lib/fide-autofill';
import { playerNameKey } from '@/components/players/player-utils';
import { isFideTitleCode } from '@swiss-magic/shared-types';
import type { Player } from '@swiss-magic/shared-types';

interface UseNewPlayerRowParams {
  tournamentId: string | undefined;
  players: Player[];
  setPlayers: Dispatch<SetStateAction<Player[]>>;
  godMode: boolean;
  /** Story 098 — Enter in the FIDE ID cell looks the ID up (chess only). */
  isChess: boolean;
}

/** FIDE-grade fields captured by an Enter-lookup in the row, carried into the
 *  create payload so a player can be registered from just their FIDE ID. */
interface RowFideLookup {
  fideId: number;
  sex: 'm' | 'f' | null;
  birthYear: number | null;
  chessTitle: string | null;
}

export function useNewPlayerRow({
  tournamentId,
  players,
  setPlayers,
  godMode,
  isChess,
}: UseNewPlayerRowParams) {
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newRating, setNewRating] = useState('');
  const [newFideId, setNewFideId] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newClub, setNewClub] = useState('');
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [rowFideLookup, setRowFideLookup] = useState<RowFideLookup | null>(null);
  const [lookingUpFide, setLookingUpFide] = useState(false);

  const newFirstNameRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation('director');
  const handleError = useApiErrorHandler();

  const newRowKey = playerNameKey(newFirstName, newLastName);
  const newRowMatchesExisting =
    newFirstName.trim() !== '' &&
    newLastName.trim() !== '' &&
    players.some((p) => playerNameKey(p.firstName, p.lastName) === newRowKey);

  async function handleAddPlayer() {
    if (!tournamentId || !newFirstName.trim() || !newLastName.trim() || addingPlayer) return;
    setAddingPlayer(true);
    // Carry the FIDE-grade fields from an Enter-lookup into the create —
    // but only if the FIDE ID cell still holds the looked-up ID (the
    // director may have typed a different ID after the lookup).
    const fide =
      rowFideLookup && String(rowFideLookup.fideId) === newFideId.trim()
        ? rowFideLookup
        : null;
    try {
      const player = await addPlayer(
        tournamentId,
        {
          firstName: newFirstName.trim(),
          lastName: newLastName.trim(),
          ...(newRating ? { rating: Number(newRating) } : {}),
          ...(newFideId ? { fideId: Number(newFideId) } : {}),
          ...(newCity.trim() ? { city: newCity.trim() } : {}),
          ...(newCountry.trim() ? { country: newCountry.trim() } : {}),
          ...(newClub.trim() ? { club: newClub.trim() } : {}),
          ...(fide?.sex ? { sex: fide.sex } : {}),
          ...(fide?.birthYear != null ? { birthYear: fide.birthYear } : {}),
          ...(fide?.chessTitle ? { chessTitle: fide.chessTitle } : {}),
        },
        godMode,
      );
      setPlayers((prev) => [...prev, player]);
      setNewFirstName('');
      setNewLastName('');
      setNewRating('');
      setNewFideId('');
      setNewCity('');
      setNewCountry('');
      setNewClub('');
      setRowFideLookup(null);
      setTimeout(() => newFirstNameRef.current?.focus(), 0);
    } catch (err: unknown) {
      handleError(err, 'Failed to add player — try again');
    } finally {
      setAddingPlayer(false);
    }
  }

  // Story 098 — register a player from just their FIDE ID: Enter in the row's
  // FIDE ID cell looks the ID up in the local mirror and prefills the row's
  // empty cells (typed values win); sex / birth year / title travel via
  // rowFideLookup into the create payload.
  async function lookupNewRowFide() {
    const id = newFideId.trim();
    if (!isChess || !/^\d+$/.test(id) || lookingUpFide) return;
    setLookingUpFide(true);
    try {
      const rec = await lookupFidePlayer(id);
      const { firstName, lastName } = splitFideName(rec.name);
      if (!newFirstName.trim() && firstName) setNewFirstName(firstName);
      if (!newLastName.trim() && lastName) setNewLastName(lastName);
      if (!newRating && rec.rating != null) setNewRating(String(rec.rating));
      if (!newCountry.trim() && rec.countryIso2) setNewCountry(rec.countryIso2);
      setRowFideLookup({
        fideId: rec.fideId,
        sex: rec.sex,
        birthYear: rec.birthYear,
        chessTitle: rec.title && isFideTitleCode(rec.title) ? rec.title : null,
      });
      showSuccessToast(t('fide_autofill.filled'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        showErrorToast(t('fide_autofill.not_found'));
      } else {
        showErrorToast(t('fide_autofill.lookup_failed'));
      }
    } finally {
      setLookingUpFide(false);
    }
  }

  function handleNewRowKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPlayer();
    }
  }

  // The FIDE ID cell repurposes Enter for the lookup (chess): the whole point
  // is registering from an ID alone, before any name exists to submit with.
  // Non-chess (or a non-numeric value) falls through to the usual add.
  function handleNewFideIdKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (isChess && /^\d+$/.test(newFideId.trim())) {
      void lookupNewRowFide();
    } else {
      void handleAddPlayer();
    }
  }

  return {
    newFirstName,
    setNewFirstName,
    newLastName,
    setNewLastName,
    newRating,
    setNewRating,
    newFideId,
    setNewFideId,
    newCity,
    setNewCity,
    newCountry,
    setNewCountry,
    newClub,
    setNewClub,
    addingPlayer,
    lookingUpFide,
    newFirstNameRef,
    newRowKey,
    newRowMatchesExisting,
    handleAddPlayer,
    handleNewRowKeyDown,
    handleNewFideIdKeyDown,
  };
}
